import styled, {css, keyframes} from 'styled-components';
import {
  PI_ASPECT,
  PI_BAR_PATH,
  PI_CARDINAL,
  PI_DASH,
  PI_FILL,
  PI_LEFT_PATH,
  PI_RIGHT_PATH,
  PI_VIEWBOX,
} from './piPaths';

/**
 * The hand-drawn PAI π mark — the same geometry as the loading spinner (see
 * `paiLoadingContent.ts`), rendered as a React component. Unlike the spinner's
 * infinitely-looping draw, this plays its stroke draw-on **once** when `draw`
 * flips true, then holds the finished glyph. When `draw` is false it renders
 * the finished glyph immediately (no animation) — used both before the finale
 * and for reduced-motion.
 */

const DASH = String(PI_DASH);

/** One-shot: sweep `stroke-dashoffset` from fully hidden to fully drawn. */
const drawOn = keyframes`
  from { stroke-dashoffset: ${DASH}; }
  to   { stroke-dashoffset: 0; }
`;

const Svg = styled.svg<{$size: number}>`
  /* Block, so the mark never baseline-aligns inside an inherited line box —
     inline SVG inside large display type (e.g. the hero title's line height)
     would be pushed far below its positioned wrapper. */
  display: block;
  width: ${({$size}) => $size}px;
  height: ${({$size}) => $size * PI_ASPECT}px;
  overflow: visible;
`;

const Ghost = styled.path<{$color: string}>`
  fill: ${({$color}) => $color};
  opacity: 0.08;
`;

/**
 * A single stroke. When `$draw` is set it starts hidden and animates on with
 * its own `$delay`/`$duration`; otherwise it renders fully drawn
 * (`stroke-dashoffset: 0`) with no animation.
 */
const Stroke = styled.path<{
  $draw: boolean;
  $delay: number;
  $duration: number;
  $color: string;
}>`
  fill: none;
  stroke: ${({$color}) => $color};
  stroke-width: 24;
  stroke-linecap: round;
  stroke-dasharray: ${DASH};
  ${({$draw, $delay, $duration}) =>
    $draw
      ? css`
          stroke-dashoffset: ${DASH};
          animation: ${drawOn} ${$duration}s ease-in-out ${$delay}s forwards;
        `
      : css`
          stroke-dashoffset: 0;
        `}
`;

/** Props for {@link PiMark}. */
export interface PiMarkProps {
  /**
   * When true, plays the one-shot stroke draw-on (bar → left leg → right leg,
   * staggered). When false, renders the finished glyph immediately with no
   * animation. Flipping false→true is what triggers the draw.
   */
  draw: boolean;
  /** Rendered width in px (height derives from the mark's aspect ratio). Omitted defaults to `170` — the loading spinner's size. */
  size?: number;
  /** Stroke and ghost-fill color. Omitted defaults to the brand cardinal (`PI_CARDINAL`). */
  color?: string;
}

/**
 * Renders the PAI π mark (see module comment).
 *
 * @param props - See {@link PiMarkProps}.
 * @return The π SVG, either drawing on (when `draw`) or settled.
 * @throws Never.
 */
export function PiMark({draw, size = 170, color = PI_CARDINAL}: PiMarkProps) {
  return (
    <Svg
      $size={size}
      viewBox={PI_VIEWBOX}
      role="img"
      aria-label="π"
      xmlns="http://www.w3.org/2000/svg"
    >
      <Ghost d={PI_FILL} $color={color} />
      {/* Staggered so the strokes draw in reading order: bar first, then the
          two legs — matching the loading spinner's draw order. */}
      <Stroke
        d={PI_BAR_PATH}
        $draw={draw}
        $delay={0}
        $duration={0.5}
        $color={color}
      />
      <Stroke
        d={PI_LEFT_PATH}
        $draw={draw}
        $delay={0.45}
        $duration={0.5}
        $color={color}
      />
      <Stroke
        d={PI_RIGHT_PATH}
        $draw={draw}
        $delay={0.85}
        $duration={0.5}
        $color={color}
      />
    </Svg>
  );
}
